import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import IngredientsCatalogService from "./IngredientsCatalogService";

/**
 * Autocomplete backed by a **frontend-filtered ingredient catalog**. On first
 * focus (of any autocomplete on the page), the full ingredient list is
 * fetched once and cached at module level; every subsequent keystroke does a
 * local prefix + substring match. No per-keystroke round-trips.
 *
 * Match rule:
 *   1. Case-insensitive prefix hits first (`name.startsWith(q)`)
 *   2. Case-insensitive substring hits next (`name.includes(q)`)
 *   Both bands sorted alphabetically. Result capped at MAX_RESULTS.
 *
 * `disabledKeys` (lowercase names already selected in *any* Food-behavior
 * list) mirrors the cross-field uniqueness rule — those rows are greyed and
 * unpickable.
 */
const MAX_RESULTS = 50;

// Module-level cache — every autocomplete on the page shares one fetch per
// session. Kept in three states so we don't spam the endpoint while the
// initial request is in flight:
//   null      → not fetched yet
//   Promise   → in flight (subsequent focuses await this)
//   Array     → resolved catalog
let CATALOG_CACHE = null;

const loadCatalog = (svc) => {
  if (Array.isArray(CATALOG_CACHE)) return Promise.resolve(CATALOG_CACHE);
  if (CATALOG_CACHE) return CATALOG_CACHE; // in-flight promise
  CATALOG_CACHE = new Promise((resolve) => {
    svc.list(
      (data) => {
        CATALOG_CACHE = Array.isArray(data) ? data : [];
        resolve(CATALOG_CACHE);
      },
      () => {
        // On failure, reset the sentinel so a later focus can retry — but
        // resolve with [] this time to avoid an unhandled rejection.
        CATALOG_CACHE = null;
        resolve([]);
      }
    );
  });
  return CATALOG_CACHE;
};

const filterCatalog = (catalog, rawQuery) => {
  const q = (rawQuery || "").trim().toLowerCase();
  if (!q) {
    // Empty query — show a stable slice sorted alphabetically.
    return [...catalog]
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .slice(0, MAX_RESULTS);
  }
  const prefix = [];
  const substring = [];
  for (const item of catalog) {
    const name = (item.name || "").toLowerCase();
    if (!name) continue;
    if (name.startsWith(q)) prefix.push(item);
    else if (name.includes(q)) substring.push(item);
  }
  const cmp = (a, b) => (a.name || "").localeCompare(b.name || "");
  prefix.sort(cmp);
  substring.sort(cmp);
  return prefix.concat(substring).slice(0, MAX_RESULTS);
};

const IngredientAutocomplete = ({ placeholder, onPick, disabled, disabledKeys }) => {
  const [query, setQuery] = useState("");
  const [catalog, setCatalog] = useState(
    Array.isArray(CATALOG_CACHE) ? CATALOG_CACHE : []
  );
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const serviceRef = useRef(null);

  if (!serviceRef.current) {
    serviceRef.current = new IngredientsCatalogService();
  }

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const ensureCatalog = () => {
    if (Array.isArray(CATALOG_CACHE)) {
      if (catalog !== CATALOG_CACHE) setCatalog(CATALOG_CACHE);
      return;
    }
    setLoading(true);
    loadCatalog(serviceRef.current).then((data) => {
      setCatalog(data);
      setLoading(false);
    });
  };

  const handleType = (e) => {
    setQuery(e.target.value);
    ensureCatalog();
    setOpen(true);
  };

  const openDropdown = () => {
    ensureCatalog();
    setOpen(true);
  };

  const results = useMemo(() => filterCatalog(catalog, query), [catalog, query]);

  const isDisabled = (row) => {
    if (!disabledKeys) return false;
    const key = (row.name || "").trim().toLowerCase();
    return !!key && disabledKeys.has(key);
  };

  const pickRow = (row) => {
    if (isDisabled(row)) return;
    const name = (row.name || "").trim();
    if (!name) return;
    onPick({
      name,
      source: "catalog",
      catalogId: row.id || "",
    });
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="cc-icd" ref={boxRef}>
      <div className="sec-search">
        <svg
          className="sec-search-icon"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <circle cx="9" cy="9" r="6" />
          <path d="M14 14l4 4" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={handleType}
          onFocus={openDropdown}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
      {open && (
        <ul className="li3-dd" role="listbox">
          {loading && <li className="li3-dd-empty">Loading ingredients…</li>}
          {!loading && results.length === 0 && (
            <li className="li3-dd-empty">No matches.</li>
          )}
          {!loading &&
            results.map((r) => {
              const key = r.id || r.name;
              const dis = isDisabled(r);
              return (
                <li
                  key={key}
                  role="option"
                  aria-selected={false}
                  aria-disabled={dis}
                  className={`li3-dd-opt${dis ? " is-disabled" : ""}`}
                  onClick={() => pickRow(r)}
                  title={dis ? "Already selected in another list" : undefined}
                >
                  <span className="icd-opt-name">{r.name}</span>
                  {r.category && <span className="cc-icd-tag">{r.category}</span>}
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
};

IngredientAutocomplete.propTypes = {
  placeholder: PropTypes.string.isRequired,
  onPick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  // Lowercase names already selected in any Food-behavior list — dropdown
  // greys them out and refuses picks.
  disabledKeys: PropTypes.instanceOf(Set),
};

export default IngredientAutocomplete;
